import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';

const COLORS = ['#00D4FF', '#22c55e', '#fb923c', '#a855f7', '#ec4899', '#eab308', '#14b8a6', '#f43f5e'];
const card = { background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 12, padding: 16 };
const title = { fontSize: 13, color: '#8b8fa3', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 };
const tooltipStyle = { background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb', fontSize: 12 };

const flagOf = (n) => ({ 'Hungary':'🇭🇺','United States':'🇺🇸','Germany':'🇩🇪','United Kingdom':'🇬🇧','France':'🇫🇷','Italy':'🇮🇹','Spain':'🇪🇸','Austria':'🇦🇹','Romania':'🇷🇴','Slovakia':'🇸🇰','Czechia':'🇨🇿','Poland':'🇵🇱','Netherlands':'🇳🇱','Switzerland':'🇨🇭','Brazil':'🇧🇷','Canada':'🇨🇦','Australia':'🇦🇺','India':'🇮🇳','Ukraine':'🇺🇦','Croatia':'🇭🇷','Serbia':'🇷🇸' }[n] || '🌍');
const fmtDate = (d) => d?.length === 8 ? `${d.slice(4,6)}-${d.slice(6,8)}` : d;
const fmtHuf = (n) => Math.round(n).toLocaleString('hu-HU') + ' Ft';
const fmtUsd = (n) => '$' + n.toFixed(2);
const fmtRelTime = (iso) => { const d=(Date.now()-new Date(iso).getTime())/1000; if(d<60)return `${Math.floor(d)}mp`; if(d<3600)return `${Math.floor(d/60)} perce`; if(d<86400)return `${Math.floor(d/3600)} órája`; return `${Math.floor(d/86400)} napja`; };
const productLabel = (id) => ({ poi_starter_15:'Starter (15)', poi_explorer_50:'Explorer (50)', poi_traveler_125:'Traveler (125)' }[id] || id);
const sourceLabel = (s) => ({ consume:'Narráció', consume_poi:'Narráció', narration:'Narráció', signup_bonus:'Regisztrációs bónusz', iap:'Vásárlás', iap_starter:'Starter csomag', iap_explorer:'Explorer csomag', iap_traveler:'Traveler csomag', promo:'Promo kód', referral:'Ajánlás', referral_bonus:'Ajánlási bónusz', comped:'Ingyenes', comped_monthly:'Havi comped', admin:'Admin', subscription:'Előfizetés', daily_discovery:'Napi felfedezés', purchase:'Vásárlás' }[s] || s);

function pivotVersions(data) {
  const byDate = {}, versions = new Set();
  data.forEach(({ date, version, activeUsers }) => { if(!byDate[date]) byDate[date]={date}; byDate[date][version]=activeUsers; versions.add(version); });
  return { rows: Object.values(byDate).sort((a,b)=>a.date.localeCompare(b.date)), versions: Array.from(versions).sort() };
}

// ── Frissítés-óra segédek ───────────────────────────────────────────────────
const clockTime = (ms) => {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return '—'; }
};
// "épp most" / "12 perce" / "3 órája" — az adatkor gyors leolvasásához
const agoText = (ms) => {
  if (!ms) return '—';
  const min = Math.floor((Date.now() - ms) / 60000);
  if (min < 1) return 'épp most';
  if (min < 60) return min + ' perce';
  const h = Math.floor(min / 60);
  if (h < 24) return h + ' órája';
  return Math.floor(h / 24) + ' napja';
};

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [poi, setPoi] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const f = (url, set) => fetch(url, { credentials: 'include' }).then(r => r.ok ? r.json() : null).then(d => d && set(d)).catch(() => {});

  useEffect(() => {
    setLoading(true); setError(null);
    fetch(`/api/analytics/overview?days=${days}`, { credentials: 'include' })
      .then(r => { if(!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [days]);

  // 🔴 KÖLTSÉG-FIX (2026-09): ez a blokk 30 másodpercenként frissült, és a
  //   mögötte lévő végpontok teljes Firestore-kollekciókat olvasnak. Egy 24/7
  //   nyitva hagyott fül napi 2880 kört futtatott → havi ~120 millió olvasás
  //   (~$76). 30 perces frissítéssel ez ~50-ed részére csökken.
  //
  //   Ráadásul HÁTTÉRBEN NEM frissítünk: ha a fül nem látszik, semmit nem
  //   kérdezünk. Amikor visszatérsz rá, azonnal frissítünk, ha lejárt az idő —
  //   így a nyitva hagyott fül gyakorlatilag ingyen van, de amit nézel, friss.
  const REFRESH_MS = 30 * 60 * 1000;
  const [lastFetch, setLastFetch] = useState(null);   // mikor kérdeztünk a szervertől
  const [refreshing, setRefreshing] = useState(false);
  const [, setClockTick] = useState(0);               // percenkénti újrarajzolás az órához
  const lastFetchRef = useRef(0);

  const loadAll = useCallback(() => {
    lastFetchRef.current = Date.now();
    setLastFetch(lastFetchRef.current);
    setRefreshing(true);
    Promise.allSettled([
      f('/api/analytics/realtime', setRealtime),
      f('/api/realtime-revenue/summary', setRevenue),
      f('/api/poi-usage/summary', setPoi),
      f('/api/funnel/summary', setFunnel),
    ]).finally(() => setRefreshing(false));
  }, []);

  useEffect(() => {
    const loadIfVisible = () => {
      if (document.visibilityState === 'visible') loadAll();
    };
    const onVisible = () => {
      // Visszatéréskor csak akkor frissítünk, ha tényleg lejárt — a fülek
      // közti kapkodás ne indítson újabb lekérdezés-sorozatot.
      if (document.visibilityState === 'visible' && Date.now() - lastFetchRef.current >= REFRESH_MS) loadAll();
    };

    loadAll();
    const i = setInterval(loadIfVisible, REFRESH_MS);
    // Az óra kijelzése percenként frissül — ez csak újrarajzolás, NEM hálózati kérés
    const clock = setInterval(() => setClockTick((x) => x + 1), 60 * 1000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(i);
      clearInterval(clock);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadAll, REFRESH_MS]);

  if (loading && !data) return <div style={{ padding: 24, color: '#8b8fa3' }}>Betöltés...</div>;
  if (error) return <div style={{ padding: 24, color: '#f43f5e' }}>Hiba: {error}</div>;
  if (!data) return null;

  const { activity, totals, versions, countries, engagement, platforms } = data;
  const vp = pivotVersions(versions);
  const actFmt = activity.map(a => ({ ...a, date: fmtDate(a.date) }));
  const verFmt = vp.rows.map(r => ({ ...r, date: fmtDate(r.date) }));
  const engMin = Math.floor(engagement.avgEngagementSec / 60);
  const engSec = Math.round(engagement.avgEngagementSec % 60);

  // A fejlécben mutatott adatkor a 25 perces frissítésű darabok közül a legrégebbi.
  // A tölcsérből SZÁNDÉKOSAN a freshAt (regisztrált + fizetett) számít bele, nem a
  // dataAt: az egyetlen 6 óránként számolt szám ("POI-t használt") a saját sorában
  // van megjelölve, ott ahol tényleg érvényes. Így a fejléc nem húzza le órákkal az
  // egész oldalt egyetlen, alig mozduló szám miatt.
  const stamps = [revenue && revenue.dataAt, poi && poi.dataAt, funnel && funnel.freshAt].filter(Boolean);
  const dataAt = stamps.length ? Math.min(...stamps) : null;
  const dataAgeMin = dataAt ? Math.floor((Date.now() - dataAt) / 60000) : 0;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ color: '#e4e6eb', margin: 0, fontSize: 24 }}>Analitika</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { value: 7, label: '7 nap' },
            { value: 30, label: '30 nap' },
            { value: 90, label: '90 nap' },
            { value: 730, label: 'Mindig' }, // 🆕 max GA4 retention (2 év)
          ].map(o => (
            <button key={o.value} onClick={() => setDays(o.value)} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #2a2e3a', background: days === o.value ? '#00D4FF' : '#1a1d27', color: days === o.value ? '#0a0d14' : '#e4e6eb', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>{o.label}</button>
          ))}
        </div>
      </div>

      {/* 🆕 Frissítés-óra — 30 perces frissítésnél tudni kell, mennyire friss az adat.
          FONTOS: a lekérés ideje NEM az adat kora. A szerver 25 percig cache-el,
          ezért a "számolva" a tényleges adatkor — azt mutatjuk. Az egyetlen kivétel
          a tölcsér "POI-t használt" száma (6 óra), az a saját sorában van jelölve. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8,
        padding: '9px 14px', marginBottom: 20, fontSize: 12, color: '#8b8fa3',
      }}>
        <span style={{ color: refreshing ? '#00D4FF' : '#22c55e' }}>
          {refreshing ? '⏳ Frissítés…' : '🕐'}
        </span>

        <span>
          Lekérve: <b style={{ color: '#e4e6eb' }}>{clockTime(lastFetch)}</b>
          <span style={{ color: '#6b7280' }}> ({agoText(lastFetch)})</span>
        </span>

        {dataAt && (
          <span title="A szerver 25 percig cache-eli az eredményt, ezért az adat ennél régebbi lehet, mint a lekérés ideje. Kivétel a tölcsér 'POI-t használt' száma: az 6 óránként számolódik, a kora ott, a saját sorában látszik.">
            Adat számolva: <b style={{ color: dataAgeMin > 60 ? '#fbbf24' : '#e4e6eb' }}>{clockTime(dataAt)}</b>
            <span style={{ color: '#6b7280' }}> ({agoText(dataAt)})</span>
          </span>
        )}

        <span style={{ color: '#6b7280' }}>
          {document.visibilityState === 'visible'
            ? `Következő: ${clockTime(lastFetch ? lastFetch + REFRESH_MS : null)}`
            : 'Háttérben szünetel'}
        </span>

        <button onClick={loadAll} disabled={refreshing}
          style={{
            marginLeft: 'auto', padding: '5px 12px', borderRadius: 6,
            border: '1px solid #2a2e3a', background: '#0a0d14', color: '#e4e6eb',
            cursor: refreshing ? 'default' : 'pointer', fontSize: 12, fontWeight: 600,
            opacity: refreshing ? 0.5 : 1,
          }}>↻ Frissítés most</button>
      </div>

      {/* Konverziós tölcsér */}
      {funnel && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={title}>Konverziós tölcsér</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
            {[
              { label: 'Regisztrált', value: funnel.registered, pct: '100', color: '#00D4FF' },
              // 🆕 Ez az EGYETLEN szám, ami 6 óránként számolódik (a teljes
              //   poi_transactions pásztázása drága). Ezért itt, a saját sorában
              //   mondjuk meg a korát — a fejléc a többi, 25 perces adatot mutatja.
              {
                label: 'POI-t használt', value: funnel.engaged, pct: funnel.rates.engagedPct, color: '#fb923c',
                note: funnel.engagedAt ? `6 óránként · ${agoText(funnel.engagedAt)}` : null,
              },
              { label: 'Fizetett', value: funnel.paid, pct: funnel.rates.paidPct, color: '#22c55e' },
            ].filter(step => step.value != null).map((step, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#e4e6eb', marginBottom: 4 }}>
                  <span>
                    {step.label}
                    {step.note && <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 6 }}>({step.note})</span>}
                  </span>
                  <span style={{ fontWeight: 700 }}>
                    {step.value}
                    {step.pct && <span style={{ color: '#8b8fa3', fontWeight: 400 }}> ({step.pct}%)</span>}
                  </span>
                </div>
                <div style={{ height: 24, background: '#2a2e3a', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ width: funnel.registered > 0 ? ((step.value / funnel.registered) * 100) + '%' : '0%', height: '100%', background: step.color, borderRadius: 6, transition: 'width 0.5s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {parseFloat(step.pct) > 8 && <span style={{ fontSize: 11, fontWeight: 600, color: '#0a0d14' }}>{step.pct}%</span>}
                  </div>
                </div>
              </div>
            ))}
            {funnel.rates.paidOfEngaged && (
              <div style={{ fontSize: 11, color: '#8b8fa3', borderTop: '1px solid #2a2e3a', paddingTop: 8 }}>
                Engaged → Paid konverzió: <strong style={{ color: '#22c55e' }}>{funnel.rates.paidOfEngaged}%</strong>
                {funnel.paidExcludingTest != null && funnel.paidExcludingTest !== funnel.paid && (
                  <span style={{ color: '#6b7280' }}>
                    {' · '}teszt-fiókok nélkül a fizetők száma <b style={{ color: '#8b8fa3' }}>{funnel.paidExcludingTest}</b>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Realtime bevétel */}
      {revenue && (<>
        <div style={{ ...title, marginBottom: 8, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> Realtime bevétel (Firestore)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Kpi label="Ma" value={fmtHuf(revenue.today.huf)} sub={`${revenue.today.count} tx · ${fmtUsd(revenue.today.usd)}`} color="#22c55e" />
          <Kpi label="7 nap" value={fmtHuf(revenue.d7.huf)} sub={`${revenue.d7.count} tx · ${fmtUsd(revenue.d7.usd)}`} color="#00D4FF" />
          <Kpi label="30 nap" value={fmtHuf(revenue.d30.huf)} sub={`${revenue.d30.count} tx · ${fmtUsd(revenue.d30.usd)}`} color="#fb923c" />
          <Kpi label="Összes" value={fmtHuf(revenue.total.huf)} sub={`${revenue.total.count} tx · ${fmtUsd(revenue.total.usd)}`} color="#a855f7" />
        </div>

        {/* 🆕 Kihagyott (nem valódi pénz) tételek — hogy a szűrés ne tűnjön bevétel-kiesésnek */}
        {revenue.excluded?.d30?.count > 0 && (
          <div style={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#8b8fa3' }}>
            <span style={{ color: '#fbbf24', fontWeight: 700 }}>Kihagyva a bevételből (30 nap): {revenue.excluded.d30.count} tx</span>
            {' · '}teszt-fiók: <b style={{ color: '#e4e6eb' }}>{revenue.excluded.d30.testAccount}</b>
            {' · '}sandbox/TestFlight: <b style={{ color: '#e4e6eb' }}>{revenue.excluded.d30.sandbox}</b>
            {revenue.excluded.d30.sandboxUsers > 0 && (
              <> (<b style={{ color: '#e4e6eb' }}>{revenue.excluded.d30.sandboxUsers}</b> valódi felhasználótól)</>
            )}
            <div style={{ marginTop: 4, lineHeight: 1.5 }}>
              A sandbox/TestFlight vásárlásoknál <b style={{ color: '#e4e6eb' }}>nem történik valódi fizetés</b>,
              ezért nem számítanak bevételnek — de valódi felhasználói szándékot jeleznek, így itt külön látszanak.
              Ma: {revenue.excluded.today?.count || 0} tx · 7 nap: {revenue.excluded.d7?.count || 0} tx.
            </div>
          </div>
        )}
      </>)}

      {/* Új regisztrációk */}
      {revenue?.newUsers && (<>
        <div style={{ ...title, marginBottom: 8, color: '#a855f7', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#a855f7', display: 'inline-block' }} /> Új regisztrációk (Firestore)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Kpi label="Ma új" value={revenue.newUsers.today} color="#22c55e" />
          <Kpi label="7 nap új" value={revenue.newUsers.d7} color="#00D4FF" />
          <Kpi label="30 nap új" value={revenue.newUsers.d30} color="#fb923c" />
          <Kpi label="Összes felh." value={revenue.newUsers.total ?? '—'} color="#a855f7" />
        </div>
      </>)}

      {revenue?.newUsersDaily?.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={title}>Új regisztrációk napi bontás (30 nap)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenue.newUsersDaily.map(d => ({ ...d, date: d.date.slice(5) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="ios" stackId="a" fill="#00D4FF" name="iOS" />
              <Bar dataKey="android" stackId="a" fill="#22c55e" name="Android" />
              <Bar dataKey="other" stackId="a" fill="#8b8fa3" name="Egyéb" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 🆕 Napi bónusz kihívás (claimDailyDiscoveryBonus → poi_transactions source='daily_discovery') */}
      {revenue?.dailyBonus && (<>
        <div style={{ ...title, marginBottom: 8, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', display: 'inline-block' }} /> 🎯 Napi bónusz kihívás (5 POI / nap)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Kpi label="Ma kapta" value={revenue.dailyBonus.today} sub={`${revenue.dailyBonus.today * 5} POI`} color="#fbbf24" />
          <Kpi label="7 nap" value={revenue.dailyBonus.d7} sub={`${revenue.dailyBonus.d7 * 5} POI`} color="#00D4FF" />
          <Kpi label="30 nap" value={revenue.dailyBonus.d30} sub={`${revenue.dailyBonus.d30 * 5} POI`} color="#22c55e" />
          <Kpi label="Összes" value={revenue.dailyBonus.total} sub={`${revenue.dailyBonus.total * 5} POI`} color="#a855f7" />
        </div>
      </>)}

      {revenue?.dailyBonusDaily?.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={title}>Napi bónusz teljesítések napi bontás (30 nap)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenue.dailyBonusDaily.map(d => ({ ...d, date: d.date.slice(5) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#fbbf24" name="Bónusz user" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* POI fogyás */}
      {poi && (<>
        <div style={{ ...title, marginBottom: 8, color: '#fb923c', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fb923c', display: 'inline-block' }} /> POI fogyás (narrációk)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Kpi label="Ma elhasznált" value={poi.today.consumed} sub={`${poi.today.credited} jóváírva`} color="#fb923c" />
          <Kpi label="7 nap" value={poi.d7.consumed} sub={`${poi.d7.credited} jóváírva`} color="#00D4FF" />
          <Kpi label="30 nap" value={poi.d30.consumed} sub={`${poi.d30.credited} jóváírva`} color="#22c55e" />
          <Kpi label="Aktív felh. (30 nap)" value={poi.uniqueUsersD30} sub="hallgatott narrációt" color="#a855f7" />
        </div>
      </>)}

      {poi?.daily?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div style={card}>
            <div style={title}>POI fogyás napi (30 nap)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={poi.daily.map(d => ({ ...d, date: d.date.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
                <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
                <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="consumed" fill="#fb923c" name="Elhasznált POI" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={card}>
            <div style={title}>POI mozgások forrás szerint (90 nap)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {poi.bySource.map((s, i) => {
                const max = Math.max(...poi.bySource.map(x => x.poi), 1);
                return (
                  <div key={s.source}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#e4e6eb', marginBottom: 4 }}>
                      <span>{sourceLabel(s.source)} <span style={{ color: '#8b8fa3' }}>× {s.count}</span></span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{s.poi} POI</span>
                    </div>
                    <div style={{ height: 6, background: '#2a2e3a', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${(s.poi / max) * 100}%`, height: '100%', background: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* GA4 KPI-k */}
      <div style={{ ...title, marginBottom: 8 }}>Felhasználók (GA4)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Kpi label="Aktív felh. (30 nap)" value={totals.d30} />
        <Kpi label="Aktív felh. (7 nap)" value={totals.d7} color="#22c55e" />
        <Kpi label="Aktív felh. (1 nap)" value={totals.d1} color="#fb923c" />
        <Kpi label="Realtime (30 perc)" value={realtime?.users ?? '—'} color="#a855f7" pulse />
        <Kpi label="Átl. engagement" value={`${engMin}p ${engSec}mp`} color="#ec4899" />
        <Kpi label="Sessions / felh." value={engagement.sessionsPerUser.toFixed(1)} color="#14b8a6" />
      </div>

      {/* Bevétel + Termékek */}
      {revenue && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div style={card}>
            <div style={title}>Bevétel napi bontás (30 nap, Ft)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revenue.daily.map(d => ({ ...d, date: d.date.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
                <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
                <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtHuf(v)} />
                <Bar dataKey="huf" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={card}>
            <div style={title}>Termék megoszlás (30 nap)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
              {revenue.products.sort((a, b) => b.huf - a.huf).map((p, i) => {
                const max = Math.max(...revenue.products.map(x => x.huf), 1);
                return (
                  <div key={p.productId}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#e4e6eb', marginBottom: 4 }}>
                      <span>{productLabel(p.productId)} <span style={{ color: '#8b8fa3' }}>× {p.count}</span></span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtHuf(p.huf)}</span>
                    </div>
                    <div style={{ height: 6, background: '#2a2e3a', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${(p.huf / max) * 100}%`, height: '100%', background: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12, fontSize: 11, color: '#8b8fa3', borderTop: '1px solid #2a2e3a', paddingTop: 8 }}>
              USD/HUF: <strong style={{ color: '#e4e6eb' }}>{revenue.rate.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Utolsó tranzakciók */}
      {revenue?.recent?.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={title}>Utolsó tranzakciók</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {revenue.recent.map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#0a0d14', borderRadius: 6, fontSize: 12, color: '#e4e6eb', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ minWidth: 60, color: '#8b8fa3' }}>{fmtRelTime(t.date)}</span>
                <span style={{ flex: 1, minWidth: 120 }}>{productLabel(t.productId)}</span>
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, background: t.platform === 'ios' ? '#1e3a5f' : '#1f3a1e', color: t.platform === 'ios' ? '#7dd3fc' : '#86efac' }}>{t.platform}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#22c55e', minWidth: 90, textAlign: 'right' }}>{fmtHuf(t.priceHuf)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Felhasználói aktivitás */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={title}>Felhasználói aktivitás ({days === 730 ? "mindig" : days + " nap"})</div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={actFmt}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
            <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
            <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
            <Tooltip contentStyle={tooltipStyle} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="activeUsers" stroke="#00D4FF" strokeWidth={2} name="Aktív" dot={{ r: 2 }} />
            <Line type="monotone" dataKey="newUsers" stroke="#22c55e" strokeWidth={2} name="Új" dot={{ r: 2 }} />
            <Line type="monotone" dataKey="sessions" stroke="#fb923c" strokeWidth={2} name="Sessions" dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* App verziók */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={title}>Aktív felhasználók app verzió szerint</div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={verFmt}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
            <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} />
            <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
            <Tooltip contentStyle={tooltipStyle} /><Legend wrapperStyle={{ fontSize: 12 }} />
            {vp.versions.map((v, i) => <Line key={v} type="monotone" dataKey={v} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />)}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Országok + Platform */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <div style={card}>
          <div style={title}>Top országok ({days === 730 ? "mindig" : days + " nap"})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {countries.slice(0, 10).map((c, i) => {
              const max = countries[0]?.activeUsers || 1;
              return (
                <div key={c.country} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#e4e6eb' }}>
                  <span style={{ width: 24 }}>{flagOf(c.country)}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.country}</span>
                  <div style={{ flex: 2, height: 6, background: '#2a2e3a', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${(c.activeUsers / max) * 100}%`, height: '100%', background: COLORS[i % COLORS.length] }} />
                  </div>
                  <span style={{ width: 40, textAlign: 'right', color: '#8b8fa3', fontVariantNumeric: 'tabular-nums' }}>{c.activeUsers}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={card}>
          <div style={title}>Platform megoszlás ({days === 730 ? "mindig" : days + " nap"})</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={platforms} dataKey="activeUsers" nameKey="platform" cx="50%" cy="50%" outerRadius={80} label={({ platform, activeUsers }) => `${platform}: ${activeUsers}`} labelLine={{ stroke: '#8b8fa3' }}>
                {platforms.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {realtime?.countries?.length > 0 && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={title}>🟢 Realtime — top országok (30 perc)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {realtime.countries.map(c => (
              <div key={c.country} style={{ background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#e4e6eb' }}>
                {flagOf(c.country)} {c.country} <span style={{ color: '#00D4FF', fontWeight: 600, marginLeft: 6 }}>{c.activeUsers}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, color = '#00D4FF', pulse = false }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 11, color: '#8b8fa3', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 8 }}>
        {pulse && <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, animation: 'pulse 2s infinite' }} />}
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#8b8fa3', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}
